import { Control, useFieldArray } from 'react-hook-form'
import { Button } from '@/common/ui/button'
import { Plus, Trash2 } from 'lucide-react'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { LuckyWheelConstraintType } from '@/schema'
import { UpdateLuckyWheelFormValues } from '../FormSchema'

interface ConditionSelectorProps {
	control: Control<UpdateLuckyWheelFormValues>
	name: 'claimConstraint'
}

const preventMinusInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
	if (e.key === '-' || e.key === 'e') {
		e.preventDefault()
	}
}

const constraintTypeOptions = [
	{
		value: LuckyWheelConstraintType.LOSS,
		label: 'ยอดเสีย (คิดต่อวัน)',
	},
	{
		value: LuckyWheelConstraintType.TURNOVER,
		label: 'เทิร์นโอเวอร์',
	},
	{
		value: LuckyWheelConstraintType.DEPOSIT_WITH_BONUS,
		label: 'ต่อยอดฝาก (คิดต่อครั้งไม่สะสม)',
	},
]

export default function ConditionSelector({
	control,
	name,
}: ConditionSelectorProps) {
	const { fields, append, remove } = useFieldArray({
		control,
		name,
	})

	const addCondition = () => {
		const usedTypes = fields.map((f) => f.constraintType)
		const availableType = constraintTypeOptions.find(
			(option) => !usedTypes.includes(option.value),
		)

		if (availableType) {
			append({
				constraintType: availableType.value,
				amount: 0,
			})
		}
	}

	return (
		<div className="mb-4 space-y-4">
			{fields.map((field, index) => (
				<div key={field.id} className="grid grid-cols-3 items-start gap-4">
					<FormField
						control={control}
						name={`${name}.${index}.constraintType`}
						render={({ field }) => (
							<FormItem>
								<FormLabel>ประเภทเงื่อนไข</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="เลือกประเภทเงื่อนไข" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{constraintTypeOptions
											.filter(
												(option) =>
													option.value === field.value ||
													!fields.some(
														(f, i) =>
															i !== index && f.constraintType === option.value,
													),
											)
											.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={control}
						name={`${name}.${index}.amount`}
						render={({ field }) => (
							<FormItem>
								<FormLabel>จำนวน</FormLabel>
								<FormControl>
									<Input
										type="number"
										onKeyDown={preventMinusInput}
										{...field}
										onChange={(e) =>
											field.onChange(
												e.target.value ? Number(e.target.value) : null,
											)
										}
										value={field.value ?? ''}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex h-[68px] items-end">
						<Button
							type="button"
							variant="destructive"
							size="icon"
							onClick={() => remove(index)}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				</div>
			))}

			{fields.length < constraintTypeOptions.length && (
				<Button type="button" variant="outline" onClick={addCondition}>
					<Plus className="mr-2 h-4 w-4" />
					เพิ่มเงื่อนไข
				</Button>
			)}
		</div>
	)
}
